import React, { useEffect, useState } from "react";
import axios from "axios";
import { API, formatErr } from "@/context/AuthContext";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus } from "@phosphor-icons/react";
import { titleCaseTR } from "@/lib/textFormat";

// Basit modül-seviye cache — aynı sayfa oturumunda birden fazla select ortak kaynak kullansın
let _cache = null;
const _subs = new Set();
function _notify(list) {
  _cache = list;
  _subs.forEach(fn => { try { fn(list); } catch (e) { /* noop */ } });
}

/**
 * Malzeme Kategorisi seçici. Kullanıcı listede olmayan bir kategori
 * eklemek istediğinde inline "Yeni Kategori" dialog'u açılır.
 *
 * Props:
 *  - value: string
 *  - onChange: (val: string) => void
 *  - placeholder?: string
 *  - allowEmpty?: boolean  (varsayılan true — "Tümü" seçeneği)
 *  - allOptionLabel?: string  (varsayılan "Tümü")
 *  - allowCreate?: boolean (varsayılan true; kullanıcı yeni kategori ekleyebilir)
 *  - testIdPrefix?: string
 *  - className?: string
 */
export default function CategorySelect({
  value,
  onChange,
  placeholder = "Kategori",
  allowEmpty = true,
  allOptionLabel = "Tümü",
  allowCreate = true,
  testIdPrefix = "category-select",
  className = "",
}) {
  const [categories, setCategories] = useState(_cache || []);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newCat, setNewCat] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const sub = (list) => setCategories(list);
    _subs.add(sub);
    if (_cache) setCategories(_cache);
    else {
      (async () => {
        try {
          const r = await axios.get(`${API}/settings/material-categories`);
          _notify(r.data?.categories || []);
        } catch (e) { /* noop */ }
      })();
    }
    return () => _subs.delete(sub);
  }, []);

  const SENTINEL_ALL = "__all__";
  const SENTINEL_ADD = "__add_new__";
  const selectValue = value ? value : (allowEmpty ? SENTINEL_ALL : "");

  const handleChange = (v) => {
    if (v === SENTINEL_ADD) {
      // Select portalı düşerken Dialog portalı ile çakışmasın —
      // React reconciler'ın Select'i unmount etmesine izin ver, sonra Dialog aç.
      setTimeout(() => setDialogOpen(true), 0);
      return;
    }
    if (v === SENTINEL_ALL) { onChange(""); return; }
    onChange(v);
  };

  const saveNew = async () => {
    const label = titleCaseTR(newCat.trim());
    if (!label) { toast.error("Kategori adı boş olamaz"); return; }
    if (categories.some(c => c.toLowerCase() === label.toLowerCase())) {
      toast.error("Bu kategori zaten mevcut");
      return;
    }
    setSaving(true);
    try {
      const next = [...categories, label];
      const r = await axios.post(`${API}/settings/material-categories`, { categories: next });
      _notify(r.data?.categories || next);
      setDialogOpen(false);
      setNewCat("");
      // Dialog kapanma animasyonu bitene kadar bekle; sonra seçimi yay.
      setTimeout(() => onChange(label), 150);
      toast.success("Kategori eklendi");
    } catch (e) { toast.error(formatErr(e)); }
    finally { setSaving(false); }
  };

  return (
    <>
      <Select value={selectValue} onValueChange={handleChange}>
        <SelectTrigger className={`rounded-none sm:rounded-sm ${className}`}
                       data-testid={`${testIdPrefix}-trigger`}>
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent>
          {allowEmpty && (
            <SelectItem value={SENTINEL_ALL} data-testid={`${testIdPrefix}-all`}>
              {allOptionLabel}
            </SelectItem>
          )}
          {categories.map(c => (
            <SelectItem key={c} value={c} data-testid={`${testIdPrefix}-item-${c}`}>
              {c}
            </SelectItem>
          ))}
          {allowCreate && (
            <SelectItem value={SENTINEL_ADD} data-testid={`${testIdPrefix}-add`}>
              <span className="flex items-center gap-1.5 text-[#1B4776] font-semibold">
                <Plus size={12} weight="bold" /> Yeni Kategori
              </span>
            </SelectItem>
          )}
        </SelectContent>
      </Select>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="rounded-none sm:rounded-sm max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-bold tracking-tight font-heading">
              Yeni Malzeme Kategorisi
            </DialogTitle>
          </DialogHeader>
          <div>
            <Label className="text-xs uppercase tracking-widest font-bold text-slate-700">
              Kategori Adı
            </Label>
            <Input
              autoFocus
              value={newCat}
              onChange={(e) => setNewCat(titleCaseTR(e.target.value))}
              placeholder="Ör: Cam Yünü"
              onKeyDown={(e) => { if (e.key === "Enter") saveNew(); }}
              data-testid={`${testIdPrefix}-new-input`}
              className="mt-2 rounded-none sm:rounded-sm"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}
                    className="rounded-none sm:rounded-sm">
              İptal
            </Button>
            <Button onClick={saveNew} disabled={saving}
                    data-testid={`${testIdPrefix}-new-save`}
                    className="rounded-none sm:rounded-sm bg-[#1B4776] hover:bg-[#153859] text-white">
              {saving ? "Kaydediliyor..." : "Ekle"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
